import {useContext, useEffect, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButtom, {atomButtonDesigns, atomButtonSizes} from '@s-ui/react-atom-button'
import AtomLabel from '@s-ui/react-atom-label'
import Context from '@s-ui/react-context'
import MoleculeButtonGroup from '@s-ui/react-molecule-button-group'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'

function getButtonTextClassNames(isDisabled) {
  return cx('mt-FormSelectToggle-buttonText', isDisabled && 'mt-FormSelectToggle-buttonText--disabled')
}

const FormSelectToggle = ({options = [], onChange = () => {}, value, label, size}) => {
  const [isFocused, setIsFocused] = useState(value)

  useEffect(() => {
    if (value !== isFocused) setIsFocused(value)
  }, [value])

  const delayToNextFrame = useDelayToNextFrame()

  const {i18n} = useContext(Context)
  const hasItems = Boolean(options.length)

  const _handleChange = ({ev, button: selectedOption = {}}) => {
    setIsFocused(selectedOption.value)
    delayToNextFrame(() => {
      const isButtonFocused = isFocused === selectedOption.value

      if (isButtonFocused) return onChange(ev, {value: null})
      onChange(ev, {value: selectedOption.value})
    })
  }

  if (!hasItems) return <></>

  return (
    <div className="mt-FormSelectToggle">
      {label && (
        <div className="mt-FormSelectToggle-label">
          <AtomLabel text={label} />
        </div>
      )}
      <div className="mt-FormSelectToggle-buttons">
        <MoleculeButtonGroup fullWidth>
          {options.map((button, index) => {
            const isButtonFocused = isFocused === button?.value

            return (
              <AtomButtom
                key={index}
                focused={isButtonFocused}
                id={index}
                isButton
                isFitted
                design={atomButtonDesigns.OUTLINE}
                onClick={ev => _handleChange({ev, button})}
                size={size}
              >
                <span className={getButtonTextClassNames(!isButtonFocused)}>{i18n.t(button.literal)}</span>
              </AtomButtom>
            )
          })}
        </MoleculeButtonGroup>
      </div>
    </div>
  )
}

FormSelectToggle.displayName = 'FormSelectToggle'

FormSelectToggle.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      literal: PropTypes.string.isRequired,
      value: PropTypes.bool.isRequired
    })
  ).isRequired,
  label: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.func,
  size: PropTypes.oneOf(Object.values(atomButtonSizes))
}

export default FormSelectToggle
